import { useEffect, useRef, useState } from 'react'
import { invitation } from '../data/invitation.js'
import { copyText } from '../lib/clipboard.js'
import { mountNaverMap } from '../lib/naverMap.js'
import styles from './Directions.module.css'

export interface DirectionsProps {
  onCopied: (message: string) => void
}

export function Directions({ onCopied }: DirectionsProps) {
  const sectionRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<HTMLDivElement>(null)
  const initialized = useRef(false)
  const [mapUnavailable, setMapUnavailable] = useState(false)

  useEffect(() => {
    const section = sectionRef.current
    const mapEl = mapRef.current
    if (section === null || mapEl === null) return

    const controller = new AbortController()
    let disposeMap: (() => void) | undefined
    let disposed = false
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !initialized.current) {
          initialized.current = true
          void mountNaverMap(mapEl, import.meta.env.VITE_NAVER_MAP_CLIENT_ID ?? '', invitation.venue, {
            signal: controller.signal,
            onFailure: () => {
              if (!disposed) setMapUnavailable(true)
            },
          })
            .then((dispose) => {
              if (disposed) dispose()
              else disposeMap = dispose
            })
            .catch(() => {
              if (!disposed) setMapUnavailable(true)
            })
          observer.disconnect()
        }
      }
    })
    observer.observe(section)
    return () => {
      disposed = true
      controller.abort()
      disposeMap?.()
      observer.disconnect()
    }
  }, [])

  async function handleAddressCopy() {
    await copyText(invitation.venue.addressForCopy)
    onCopied('주소가 복사되었어요')
  }

  return (
    <div ref={sectionRef} className={styles.wrap}>
      <div className={styles.divider}>
        <span className={styles.line} />
        <span className={styles.dot} />
        <span className={styles.line} />
      </div>
      <div className={styles.eyebrow}>location</div>
      <h2 className={styles.title}>오시는 길</h2>
      <div className={styles.venue}>
        {invitation.venue.name} · {invitation.venue.hall}
      </div>
      <div className={styles.mapCard}>
        <div ref={mapRef} className={styles.mapEl} aria-label={`${invitation.venue.name} 지도`} />
        {mapUnavailable ? (
          <a
            href={invitation.mapApps[0].href}
            target="_blank"
            rel="noopener"
            className={styles.mapFallback}
          >
            네이버 지도에서 장소 보기
          </a>
        ) : null}
      </div>
      <div className={styles.addressBlock}>
        <div className={styles.label}>주소</div>
        <div className={styles.addressRow}>
          <span className={styles.addressText}>{invitation.venue.address}</span>
          <button type="button" className={styles.copyBtn} onClick={() => void handleAddressCopy()}>
            복사
          </button>
        </div>
      </div>
      <div className={styles.transitGrid}>
        {invitation.transit.map((row) => (
          <div key={row.label}>
            <div className={styles.label}>{row.label}</div>
            <div className={styles.transitText}>{row.text}</div>
          </div>
        ))}
      </div>
      <div className={styles.linksRow}>
        <span className={styles.linksLabel}>지도 앱</span>
        <span className={styles.linksWrap}>
          {invitation.mapApps.map((m) => (
            <a key={m.label} href={m.href} target="_blank" rel="noopener" className={styles.mapAppLink}>
              {m.label}
            </a>
          ))}
        </span>
      </div>
    </div>
  )
}
